import { useCallback, useEffect, useMemo, useState } from "react";
import { Col } from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faMagnifyingGlass,
  faPenToSquare,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";

import { APISD } from "../../../../../../../config/apisd";
import ModalAdd from "../../modal/add";
import ModalUpdate from "../../modal/update";
import ToastError from "../../../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../../../NotificationToast/ToastSuccess";

import "bulma/css/bulma.css";
import "../../../../../../../index.css";

const pageStyles = `
  @keyframes aksesAbsenShimmer {
    0% { background-position: -468px 0; }
    100% { background-position: 468px 0; }
  }

  .akses-absen-shimmer {
    animation: aksesAbsenShimmer 1.2s linear infinite;
    background: linear-gradient(
      to right,
      #ddeaff 8%,
      #f4f8ff 18%,
      #ddeaff 33%
    );
    background-size: 800px 104px;
  }

  .akses-absen-toolbar {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 18px;
    
    border-radius: 16px;
    background: #ffffff;
  }

  .akses-absen-search {
    width: min(100%, 380px);
  }

  .akses-absen-add-button {
    display: inline-flex;
    min-height: 42px;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 0;
    background: #3f5bea;
    color: #ffffff;
    font-weight: 700;
  }

  .akses-absen-add-button:hover,
  .akses-absen-add-button:focus {
    background: #354fd5;
    color: #ffffff;
  }

  .akses-absen-skeleton-search {
    width: min(100%, 380px);
    height: 44px;
    border-radius: 12px;
  }

  .akses-absen-skeleton-button {
    width: 190px;
    height: 42px;
    border-radius: 10px;
  }

  .akses-absen-table {
    min-width: 720px;
  }

  .akses-absen-col-username {
    width: 25%;
  }

  .akses-absen-col-school {
    width: 30%;
  }

  .akses-absen-col-branch {
    width: 25%;
  }

  @media (max-width: 780px) {
    .akses-absen-toolbar {
      align-items: stretch;
      flex-direction: column;
      gap: 12px;
      padding: 15px;
      border-radius: 13px;
    }

    .akses-absen-search,
    .akses-absen-add-button,
    .akses-absen-skeleton-search,
    .akses-absen-skeleton-button {
      width: 100%;
    }

    .akses-absen-table {
      min-width: 680px;
    }
  }
`;

export default function AksesAbsensi() {
  document.title = "Akses Absensi";

  const token = localStorage.getItem("token");
  const storageLevel = localStorage.getItem("level");
  const location = useLocation();
  const navigate = useNavigate();

  const [getData, setGetData] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [tablePage, setTablePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isFetching, setIsFetching] = useState(false);

  const [id, setId] = useState(null);
  const [dataUpdate, setDataUpdate] = useState(null);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalUpdate, setModalUpdate] = useState(false);

  const [permission, setPermission] = useState({
    create: 0,
    read: 0,
    update: 0,
    delete: 0,
  });

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  useEffect(() => {
    const rolePermissions =
      JSON.parse(localStorage.getItem("role_permission")) || [];

    if (storageLevel === "developer" || storageLevel === "superadmin") {
      setPermission({
        create: 1,
        read: 1,
        update: 1,
        delete: 1,
      });
      return;
    }

    const foundPermission = rolePermissions.find(
      (item) => item.menu?.url === location.pathname
    );

    if (!foundPermission) {
      navigate("/dashboard");
      return;
    }

    setPermission({
      create: foundPermission.create,
      read: foundPermission.read,
      update: foundPermission.update,
      delete: foundPermission.delete,
    });
  }, [location.pathname, navigate, storageLevel]);

  const GetResponseData = useCallback(async () => {
    setSearchKeyword("");
    setIsFetching(true);

    try {
      const response = await APISD.get(
        "/api/absen-auth?page=1&limit=1000&ascending=1&search=",
        fetchParams
      );

      if (response?.status === 200) {
        setGetData(response?.data?.data || []);
      }
    } catch (error) {
      setGetData([]);

      ToastError.fire({
        icon: "error",
        title: error?.response?.data?.message || "Gagal memuat data",
      });
    } finally {
      setIsFetching(false);
    }
  }, [fetchParams]);

  useEffect(() => {
    GetResponseData();
  }, [GetResponseData]);

  const deleteById = (selectedId) => {
    Swal.fire({
      title: "Apakah kamu yakin?",
      text: "Data yang dihapus tidak dapat dikembalikan.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, Hapus",
      cancelButtonText: "Batal",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        try {
          const response = await APISD.delete(
            `/api/absen-auth/${selectedId}`,
            fetchParams
          );

          if (response?.data?.error) {
            throw new Error(response?.data?.message);
          }

          return response;
        } catch (error) {
          Swal.showValidationMessage(
            error?.response?.data?.message ||
              error?.message ||
              "Gagal menghapus data"
          );

          return false;
        }
      },
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      ToastSuccess.fire({
        icon: "success",
        title: "Data berhasil dihapus",
      });

      GetResponseData();
    });
  };

  const viewModalUpdate = (item) => {
    setId(item?.id);
    setDataUpdate(item);
    setModalUpdate(true);
  };

  const nameCollator = useMemo(
    () => new Intl.Collator("id", { sensitivity: "base", numeric: true }),
    []
  );

  const filteredData = useMemo(() => {
    const query = searchKeyword.trim().toLowerCase();
    const source = Array.isArray(getData) ? getData : [];

    const filtered = query
      ? source.filter((item) => {
          const username = String(item?.username || "").toLowerCase();
          const school = String(item?.ysb_school_id || "").toLowerCase();
          const branch = String(item?.ysb_branch_id || "").toLowerCase();

          return (
            username.includes(query) ||
            school.includes(query) ||
            branch.includes(query)
          );
        })
      : source;

    return [...filtered].sort((first, second) =>
      nameCollator.compare(
        String(first?.username || "").trim(),
        String(second?.username || "").trim()
      )
    );
  }, [getData, nameCollator, searchKeyword]);

  const totalRows = filteredData.length;

  const totalPages = useMemo(() => {
    const pages = Math.ceil(totalRows / Math.max(1, rowsPerPage));
    return pages <= 0 ? 1 : pages;
  }, [rowsPerPage, totalRows]);

  useEffect(() => {
    setTablePage(1);
  }, [getData, searchKeyword]);

  useEffect(() => {
    if (tablePage > totalPages) {
      setTablePage(totalPages);
    }

    if (tablePage < 1) {
      setTablePage(1);
    }
  }, [tablePage, totalPages]);

  const paginatedData = useMemo(() => {
    const safeRows = Math.max(1, rowsPerPage);
    const start = (tablePage - 1) * safeRows;
    return filteredData.slice(start, start + safeRows);
  }, [filteredData, rowsPerPage, tablePage]);

  const pageNumbers = useMemo(() => {
    const maxButtons = 5;
    const pages = [];

    if (totalPages <= maxButtons) {
      for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
        pages.push(pageNumber);
      }

      return pages;
    }

    const left = Math.max(1, tablePage - 1);
    const right = Math.min(totalPages, tablePage + 1);

    pages.push(1);

    if (left > 2) {
      pages.push("...");
    }

    for (let pageNumber = left; pageNumber <= right; pageNumber += 1) {
      if (pageNumber !== 1 && pageNumber !== totalPages) {
        pages.push(pageNumber);
      }
    }

    if (right < totalPages - 1) {
      pages.push("...");
    }

    pages.push(totalPages);

    return pages.filter(
      (value, index, array) => array.indexOf(value) === index
    );
  }, [tablePage, totalPages]);

  const goToPage = (nextPage) => {
    const destination = Math.min(
      Math.max(1, Number(nextPage) || 1),
      totalPages
    );

    setTablePage(destination);
  };

  return (
    <div className="body module-page">
      <style>{pageStyles}</style>

      {modalAdd && (
        <ModalAdd
          GetResponseData={GetResponseData}
          show={modalAdd}
          onHide={() => setModalAdd(false)}
        />
      )}

      {modalUpdate && (
        <ModalUpdate
          GetResponseData={GetResponseData}
          id={id}
          dataUpdate={dataUpdate}
          show={modalUpdate}
          onHide={() => setModalUpdate(false)}
        />
      )}

      <div className="module-content">
        {isFetching ? (
          <div className="akses-absen-toolbar">
            <div className="akses-absen-shimmer akses-absen-skeleton-search" />
            {permission.create === 1 && (
              <div className="akses-absen-shimmer akses-absen-skeleton-button" />
            )}
          </div>
        ) : (
          <div className="akses-absen-toolbar">
            <div className="akses-absen-search">
              <div className="module-search-wrap">
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  className="module-search-icon"
                />
                <input
                  type="search"
                  className="module-search-input"
                  placeholder="Cari username, sekolah, atau cabang..."
                  value={searchKeyword}
                  onChange={(event) => setSearchKeyword(event.target.value)}
                />
              </div>
            </div>

            {permission.create === 1 && (
              <button
                type="button"
                className="btn btn-create module-header-button"
                onClick={() => setModalAdd(true)}
              >
                <FontAwesomeIcon icon={faPlus} />
                Tambah Akses Absensi
              </button>
            )}
          </div>
        )}

        <Col xl="12" sm="12" className="module-column">
          <div className="module-table-card">
            <div className="module-table-scroll">
              <table className="module-table akses-absen-table">
                <thead>
                  <tr>
                    <th className="module-col-number">No</th>
                    <th className="akses-absen-col-username">Username</th>
                    <th className="akses-absen-col-school">Sekolah</th>
                    <th className="akses-absen-col-branch">Cabang</th>
                    <th className="module-col-action">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {isFetching ? (
                    <tr>
                      <td colSpan="5" className="module-empty-state">
                        <div className="module-empty-state-content">
                          Memuat data...
                        </div>
                      </td>
                    </tr>
                  ) : paginatedData.length > 0 ? (
                      paginatedData.map((row, index) => (
                        <tr key={row?.id || index}>
                          <td className="module-col-number">
                            <span className="module-number-badge">
                              {(tablePage - 1) * rowsPerPage + index + 1}
                            </span>
                          </td>
                          <td>
                            <span className="module-primary-text">
                              {row?.username || "-"}
                            </span>
                          </td>
                          <td>
                            <span className="module-secondary-text">
                              {row?.ysb_school_id || "-"}
                            </span>
                          </td>
                          <td>
                            <span className="module-secondary-text">
                              {row?.ysb_branch_id || "-"}
                            </span>
                          </td>
                          <td className="module-col-action">
                            <div className="module-action-wrap">
                              {permission.update === 1 && (
                                <button
                                  type="button"
                                  className="module-action-button module-action-edit"
                                  onClick={() => viewModalUpdate(row)}
                                  aria-label={`Ubah akses ${row?.username || ""}`}
                                  title="Ubah"
                                >
                                  <FontAwesomeIcon icon={faPenToSquare} />
                                </button>
                              )}

                              {permission.delete === 1 && (
                                <button
                                  type="button"
                                  className="module-action-button module-action-delete"
                                  onClick={() => deleteById(row?.id)}
                                  aria-label={`Hapus akses ${row?.username || ""}`}
                                  title="Hapus"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              )}

                              {permission.update !== 1 &&
                                permission.delete !== 1 && (
                                  <span className="module-muted-text">-</span>
                                )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="module-empty-state">
                          <div className="module-empty-state-content">
                            Data tidak ditemukan
                          </div>
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>

            {!isFetching && filteredData.length > 0 && (
              <div className="module-pagination">
                  <div className="module-pagination-left">
                    <span className="module-page-info">
                      Menampilkan{" "}
                      <b>
                        {Math.min(
                          (tablePage - 1) * rowsPerPage + 1,
                          totalRows
                        )}
                      </b>
                      -
                      <b>{Math.min(tablePage * rowsPerPage, totalRows)}</b>{" "}
                      dari <b>{totalRows}</b> data
                    </span>

                    <select
                      className="module-page-size"
                      value={rowsPerPage}
                      onChange={(event) => {
                        setRowsPerPage(Number(event.target.value) || 10);
                        setTablePage(1);
                      }}
                    >
                      <option value={10}>10 / halaman</option>
                      <option value={25}>25 / halaman</option>
                      <option value={50}>50 / halaman</option>
                      <option value={100}>100 / halaman</option>
                    </select>
                  </div>

                  <div className="module-pagination-right">
                    <button
                      type="button"
                      className="module-page-nav"
                      onClick={() => goToPage(tablePage - 1)}
                      disabled={tablePage <= 1}
                      aria-label="Halaman sebelumnya"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                      Prev
                    </button>

                    <div className="module-page-numbers">
                      {pageNumbers.map((page, index) =>
                        page === "..." ? (
                          <span
                            key={`page-dots-${index}`}
                            className="module-page-dots"
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            type="button"
                            key={`page-${page}`}
                            className={`module-page-number ${
                              page === tablePage ? "active" : ""
                            }`}
                            onClick={() => goToPage(page)}
                          >
                            {page}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      type="button"
                      className="module-page-nav"
                      onClick={() => goToPage(tablePage + 1)}
                      disabled={tablePage >= totalPages}
                      aria-label="Halaman berikutnya"
                    >
                      Next
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
              </div>
            )}
          </div>
        </Col>
      </div>
    </div>
  );
}
